import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import DemoBanner from './components/DemoBanner';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Partners from './pages/Partners';
import Treatments from './pages/Treatments';
import TreatmentDetail from './pages/TreatmentDetail';
import ProcedureDetail from './pages/ProcedureDetail';
import Doctors from './pages/Doctors';
import DoctorDetail from './pages/DoctorDetail';
import Hospitals from './pages/Hospitals';
import HospitalDetail from './pages/HospitalDetail';
import MedicalVisa from './pages/MedicalVisa';
import Wellness from './pages/Wellness';
// Service Pages
import AirportPickup from './pages/services/AirportPickup';
import LanguageInterpreter from './pages/services/LanguageInterpreter';
import SurroundingTours from './pages/services/SurroundingTours';
import NursingCare from './pages/services/NursingCare';
import Physiotherapist from './pages/services/Physiotherapist';
import TravelAccommodation from './pages/services/TravelAccommodation';
import VideoConsultations from './pages/services/VideoConsultations';
import PatientDashboard from './pages/patient/Dashboard';
import DoctorLayout from './pages/doctor/DoctorLayout';
import DoctorHome from './pages/doctor/DoctorHome';
import DoctorPatients from './pages/doctor/DoctorPatients';
import DoctorInquiries from './pages/doctor/DoctorInquiries';
import DoctorAppointments from './pages/doctor/DoctorAppointments';
import DoctorSettings from './pages/doctor/DoctorSettings';
import DoctorRecordsAI from './pages/doctor/DoctorRecordsAI';
import VideoConsultationPage from './pages/doctor/VideoConsultationPage';
import HospitalDashboard from './pages/hospital/Dashboard';
import CaseManagerDashboard from './pages/case_manager/Dashboard';
import AdminLogin from './pages/admin/AdminLogin';
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import PatientManagement from './pages/admin/PatientManagement';
import DoctorManagementFull from './pages/admin/DoctorManagementFull';
import HospitalManagementFull from './pages/admin/HospitalManagementFull';
import TreatmentManagement from './pages/admin/TreatmentManagement';
import FinancialManagement from './pages/admin/FinancialManagement';
import SystemSettings from './pages/admin/SystemSettings';
import AppointmentManagement from './pages/admin/AppointmentManagement';
import ContentManagement from './pages/admin/ContentManagement';
import SupportDisputes from './pages/admin/SupportDisputes';
import Analytics from './pages/admin/Analytics';
import TaskManagement from './pages/admin/TaskManagement';
import PatientInquiries from './pages/admin/PatientInquiries';
import ClassifiedInquiries from './pages/admin/ClassifiedInquiries';
import BiginDashboard from './pages/admin/BiginDashboard';
import DynamicPipeline from './pages/admin/DynamicPipeline';
import Integrations from './pages/admin/Integrations';
import AISettings from './pages/admin/AISettings';
import { isAuthenticated, getUserRole } from './utils/auth';
import './App.css';

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles, isAdminRoute = false }) => {
  if (!isAuthenticated()) {
    return <Navigate to={isAdminRoute ? "/admin/login" : "/login"} replace />;
  }
  
  const userRole = getUserRole();
  if (allowedRoles && !allowedRoles.includes(userRole)) {
    return <Navigate to={isAdminRoute ? "/admin/login" : "/login"} replace />;
  }
  
  return children;
};

function App() {
  return (
    <ThemeProvider>
    <LanguageProvider>
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <DemoBanner />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/partners" element={<Partners />} />
        <Route path="/treatments" element={<Treatments />} />
        <Route path="/treatments/:treatmentId" element={<TreatmentDetail />} />
        <Route path="/procedures/:procedureId" element={<ProcedureDetail />} />
        <Route path="/doctors" element={<Doctors />} />
        <Route path="/doctors/:doctorId" element={<DoctorDetail />} />
        <Route path="/hospitals" element={<Hospitals />} />
        <Route path="/hospitals/:hospitalId" element={<HospitalDetail />} />
        <Route path="/medical-visa" element={<MedicalVisa />} />
        <Route path="/wellness" element={<Wellness />} />
        
        {/* Service Pages */}
        <Route path="/services/airport-pickup" element={<AirportPickup />} />
        <Route path="/services/language-interpreter" element={<LanguageInterpreter />} />
        <Route path="/services/surrounding-tours" element={<SurroundingTours />} />
        <Route path="/services/nursing-care" element={<NursingCare />} />
        <Route path="/services/physiotherapist" element={<Physiotherapist />} />
        <Route path="/services/travel-accommodation" element={<TravelAccommodation />} />
        <Route path="/services/video-consultations" element={<VideoConsultations />} />
        
        {/* Patient Routes */}
        <Route 
          path="/patient/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['patient']}>
              <PatientDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Doctor Routes */}
        <Route 
          path="/doctor"
          element={
            <ProtectedRoute allowedRoles={['doctor']}>
              <DoctorLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/doctor/dashboard" replace />} />
          <Route path="dashboard" element={<DoctorHome />} />
          <Route path="patients" element={<DoctorPatients />} />
          <Route path="ai-records" element={<DoctorRecordsAI />} />
          <Route path="inquiries" element={<DoctorInquiries />} />
          <Route path="appointments" element={<DoctorAppointments />} />
          <Route path="quotes" element={<DoctorInquiries />} />
          <Route path="consultations" element={<DoctorAppointments />} />
          <Route path="video-consultation" element={<VideoConsultationPage />} />
          <Route path="availability" element={<DoctorAppointments />} />
          <Route path="earnings" element={<DoctorHome />} />
          <Route path="reviews" element={<DoctorHome />} />
          <Route path="settings" element={<DoctorSettings />} />
        </Route>
        
        {/* Hospital Routes */}
        <Route 
          path="/hospital/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['hospital']}>
              <HospitalDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Case Manager Routes */}
        <Route 
          path="/case_manager/dashboard" 
          element={
            <ProtectedRoute allowedRoles={['case_manager']}>
              <CaseManagerDashboard />
            </ProtectedRoute>
          } 
        />
        
        {/* Admin Portal Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route 
          path="/admin" 
          element={
            <ProtectedRoute allowedRoles={['super_admin', 'admin', 'doctor_verifier', 'finance_admin', 'moderator', 'support_admin']} isAdminRoute={true}>
              <AdminLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<AdminDashboard />} />
          <Route path="super-admin" element={<SuperAdminDashboard />} />
          <Route path="patients" element={<PatientManagement />} />
          <Route path="doctors" element={<DoctorManagementFull />} />
          <Route path="hospitals" element={<HospitalManagementFull />} />
          <Route path="treatments" element={<TreatmentManagement />} />
          <Route path="appointments" element={<AppointmentManagement />} />
          <Route path="financial" element={<FinancialManagement />} />
          <Route path="moderation" element={<ContentManagement />} />
          <Route path="system" element={<SystemSettings />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="support" element={<SupportDisputes />} />
          <Route path="tasks" element={<TaskManagement />} />
          <Route path="inquiries" element={<PatientInquiries />} />
          <Route path="classified" element={<ClassifiedInquiries />} />
          <Route path="bigin" element={<BiginDashboard />} />
          {/* Dynamic Pipeline Routes - Zoho Bigin Pipelines */}
          <Route path="pipeline/:pipelineSlug" element={<DynamicPipeline />} />
          {/* Legacy routes redirect to new dynamic pipeline */}
          <Route path="pre-arrival" element={<Navigate to="/admin/pipeline/pre-arrival" replace />} />
          <Route path="integrations" element={<Integrations />} />
          <Route path="ai-settings" element={<AISettings />} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
    </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
